import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Droplets, Loader2 } from "lucide-react";
import { toast } from "sonner";

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 grid-dots">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center"><Droplets className="h-5 w-5" /></div>
          <span className="font-heading font-extrabold text-xl">PlumbFlow</span>
        </Link>
        <div>
          <h2 className="text-3xl font-bold font-heading leading-tight">The lead CRM built for plumbing pros.</h2>
          <p className="mt-4 text-slate-400">Capture leads, follow up automatically, and book more jobs — all from one dashboard.</p>
        </div>
        <p className="text-sm text-slate-500">© {new Date().getFullYear()} PlumbFlow</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center"><Droplets className="h-5 w-5 text-white" /></div>
            <span className="font-heading font-extrabold text-xl">PlumbFlow</span>
          </Link>
          <Card className="p-8 shadow-sm border-slate-200">
            <h1 className="text-2xl font-bold font-heading">{title}</h1>
            <p className="text-slate-500 mt-1 text-sm">{subtitle}</p>
            <div className="mt-6">{children}</div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export { AuthShell };

export default function Login() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      await loginWithToken(data.token, data.user);
      toast.success("Welcome back!");
      navigate("/app/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Log in" subtitle="Welcome back to PlumbFlow">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className="mt-1" placeholder="you@company.com" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline" data-testid="login-forgot-link">Forgot?</Link>
          </div>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className="mt-1" placeholder="••••••••" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full h-11 bg-blue-600 hover:bg-blue-700">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        No account? <Link to="/register" className="text-blue-600 font-medium hover:underline" data-testid="login-register-link">Start free trial</Link>
      </p>
    </AuthShell>
  );
}
